import React, { useRef, useState, useEffect } from "react";
import {Button, TextBox, ToastProvider, Select, useToast, ProgressIndicator} from "@veneer/core";
import ResultTable from "./components/ResultTable/ResultTable";
import fetchPrintHead from "../../api/printhead";
import {Input} from "reactstrap";

// Utility function to decode Base64 URL
const base64UrlDecode = (base64Url) => {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    return decodeURIComponent(escape(atob(base64 + padding)));
};

// Function to parse JWT token
const parseJwt = (token) => {
    const [header, payload, signature] = token.split('.');

    try {
        return {
            header: JSON.parse(base64UrlDecode(header)),
            payload: JSON.parse(base64UrlDecode(payload)),
            signature
        };
    } catch (e) {
        console.error("Error parsing JWT:", e);
        return { header: null, payload: null, signature: null };
    }
};
function removeCookie(name, path = '/') {

    const cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;

    // Remove the cookie
    document.cookie = cookieString;
}


function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
}

const handleLogout = () => {
    removeCookie('auth-token');
    localStorage.setItem('redirectURL', window.location.href);
    const logoutUrl = `${process.env.REACT_APP_PHPSC_LOGOUT_AUTH_URI}/?post_logout_redirect_uri=${process.env.REACT_APP_PHPSC_LOGOUT_REDIRECT_URI}/&state=sign_out`
                .replace(/([^:]\/)\/+/g, "$1");
    window.location.href = logoutUrl;
};


const Home = () => {
    const caseIdRef = useRef(null);
    const codeAuthorizeRef = useRef(null);
    const [caseIdError, setCaseIdError] = useState("");
    const [codeAuthorizeError, setCodeAuthorizeError] = useState("");
    const [showResult, setShowResult] = useState(false);
    const [caseId, setCaseId] = useState("");
    const [codeAuthorize, setCodeAuthorize] = useState("");
    const [loading, setLoading] = useState(true);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [userRole, setUserRole] = useState([]);
    const [userRegion, setUserRegion] = useState([]);
    const [showRoleSelector, setShowRoleSelector] = useState(false);
    const [isHPUser, setIsHPUser] = useState(false);
    const [regionError, setRegionError] = useState("");
    const { addToast } = useToast();
    const [description, setDescription] = useState( '');
    const [hasRedirected, setHasRedirected] = useState(false);

    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    };

    const loaderContainerStyle = {
        width: '300px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const roles = [
        { value: 'Service Authorized Partner', label: 'Service Authorized Partner' },
        { value: 'Authorized Return Center', label: 'Authorized Return Center' }
    ];

    const regionOptions = [
        { value: 'EMEA', label: 'EMEA' },
        { value: 'NA', label: 'NA' },
        { value: 'APJ', label: 'APJ' },
        { value: 'LA', label: 'LA' }
    ];

    const ROLE_MAP = {
        1: 'Admin',
        2: 'Service Authorized Partner',
        3: 'Call Center',
        4: 'Authorized Return Center'
    };
    const reverseRoleMap = Object.fromEntries(
        Object.entries(ROLE_MAP).map(([key, value]) => [value, Number(key)])
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const storedToken = getCookie('auth-token');

                if (storedToken) {
                    const { header,payload } = parseJwt(storedToken);
                    if(header.userName) {
                        setTokenInfo(header);
                    }
                    else{
                        setTokenInfo(payload);
                    }
                    const redirectURL = localStorage.getItem('redirectURL');
                    if (redirectURL && !hasRedirected) {
                        localStorage.removeItem('redirectURL');
                        setHasRedirected(true);
                        window.location.href = redirectURL;
                        return;
                    }

                    const tokenExpiration = header.exp || payload.exp; // Convert expires_in from seconds to milliseconds
                    const currentTime = new Date().getTime();
                    const timestampInSeconds = Math.floor(currentTime / 1000);

                    if (timestampInSeconds < tokenExpiration) {
                        setLoading(false);

                        // Convert role ID to role name
                        const storedUserRoleId = header.profile || payload.profile;
                        const storedUserRole = ROLE_MAP[storedUserRoleId];
                        const storedUserRegion = header.region || payload.region;
                        const storedUserStatus = header.status || payload.status;
                        if (storedUserStatus && (storedUserStatus.toLowerCase() !== "active")) {
                            window.location.href = '/unauthorized';
                        }


                        if (!storedUserStatus) {
                            let isHP;
                            if(header.userName) {
                                isHP = header.userName.endsWith('@hp.com');
                            }
                            else {
                                isHP = payload.userName.endsWith('@hp.com');
                            }
                            if (isHP) {
                                setUserRole([ROLE_MAP[3]]);
                                setIsHPUser(true)
                            }
                            setShowRoleSelector(true);
                        } else {
                            setUserRole([storedUserRole]);
                            setUserRegion([storedUserRegion]);
                        }
                    } else {
                        handleLogout();
                    }
                }
                else{
                    window.location.href = '/login';
                }


            } catch (error) {
                console.error('Error fetching data from backend:', error.message);
            }finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [hasRedirected]);

    const handleRoleSubmit = async (event) => {
        event.preventDefault();
        if (userRole.length > 0 && userRegion.length > 0 && description.length > 0) {
            try {
                const profileValue = reverseRoleMap[userRole[0] || ''];
                const payload = {
                    description: description,
                    profile: typeof profileValue === 'string' ? profileValue : String(profileValue),
                    region: userRegion.join(', '),
                    user_email: tokenInfo.userName,
                    hpidUserId: tokenInfo.hpUserId
                };

                const response = await fetch(`${process.env.REACT_APP_PHPSC_BACK_URL}/phpsc-backend/api/v1/users/register`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                const responseData = await response.json();

                if (!response.ok) {
                    const errorMessages = Array.isArray(responseData.detail)
                        ? responseData.detail.map(error => error.msg).join(', ') // Join messages if multiple
                        : responseData.detail; // Fallback message

                    addToast({
                        text: errorMessages,
                        type: 'negative',
                        timeout: 3,
                        id: Date.now(),
                    });
                } else {
                    addToast({
                        text: 'Successfully registered the user',
                        type: 'positive',
                        timeout: 3,
                        id: Date.now(),
                    });
                    localStorage.setItem('userRole', userRole[0]);
                    localStorage.setItem('userRegion', userRegion[0]);
                    removeCookie('auth-token');
                    setShowRoleSelector(false);
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);

                };

            } catch (error) {
                console.error('Error updating user:', error);
                addToast({
                    text: 'Failed to register user due to network error',
                    type: 'negative',
                    timeout: 3,
                    id: Date.now(),
                });
            }
        } else {
            setRegionError("All fields are required");
        }
    };

    const handleRoleChange = (selectedOption) => {
        setUserRole([selectedOption.value]);
    };

    const handleRegionChange = (selectedOption) => {
        setUserRegion([selectedOption.value]);
    };

    const [apiData, setApiData] = useState(null);

// Update handleSubmit to set the apiData
    const handleSubmit = async (event) => {
        event.preventDefault();

        const codeAuthorizeValue = codeAuthorizeRef.current.value.trim();
        const caseID = ''; // Provide a case ID if available

        if (codeAuthorizeValue === "") {
            setCodeAuthorizeError("Code to authorize is required");
            return;
        } else {
            setCodeAuthorizeError("");
        }

        try {
            const response = await fetchPrintHead(codeAuthorizeValue, caseID);
            setApiData(response); // Store API data in state

            // Update the result display logic
            setShowResult(true);
        } catch (error) {
            setCodeAuthorizeError(error.message || "An error occurred while fetching data");
        }
    };

    const onClear = () => {
        setUserRole([]);
        setUserRegion([]);
    };

    return (
        <ToastProvider>
            <div>
                {loading && (
                    <div style={overlayStyle}>
                        <div style={loaderContainerStyle}>
                            <ProgressIndicator appearance="linear" behavior="indeterminate" />
                        </div>
                    </div>
                )}
                {showRoleSelector ? (
                    <div className="container-fluid">
                        <div className="card-wrapper">
                            <form className="form" onSubmit={handleRoleSubmit}>
                                <div className="form-group">
                                    <label htmlFor="user-region" className="form-label">Email*</label>
                                    <Input
                                        id="user-email"
                                        label="Email"
                                        className={"form-input"}
                                        placement="bottom"
                                        disabled
                                        helperTextVisibility="auto"
                                        className={"form-input"}
                                        value={tokenInfo.userName}
                                    />
                                </div>
                                {!isHPUser && (
                                    <div className="form-group">
                                        <label htmlFor="user-role" className="form-label">Select Role*</label>
                                        <Select
                                            options={roles}
                                            id="user-role"
                                            label="Select Role"
                                            placement="bottom"
                                            helperTextVisibility="auto"
                                            onChange={handleRoleChange}
                                            onClear={onClear}
                                            value={userRole}
                                        />
                                    </div>
                                )}

                                {isHPUser && (
                                    <div className="form-group">
                                        <label htmlFor="user-role" className="form-label"> Role*</label>
                                        <Input
                                            options={roles}
                                            value={[ROLE_MAP[3]]}
                                            className={"form-input"}
                                            disabled
                                        />
                                    </div>
                                )}
                                <div className="form-group">
                                    <label htmlFor="user-region" className="form-label">Select Region*</label>
                                    <Select
                                        options={regionOptions}
                                        id="user-region"
                                        label="Select Region"
                                        placement="bottom"
                                        helperTextVisibility="auto"
                                        onChange={handleRegionChange}
                                        onClear={onClear}
                                        value={userRegion}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="user-description" className="form-label">Description*</label>
                                    <Input
                                        id="description"
                                        label="description"
                                        className={"form-input"}
                                        placement="bottom"
                                        helperTextVisibility="auto"
                                        className={"form-input"}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                                {regionError && (
                                    <div className="invalid-feedback" style={{ color: 'red', fontSize: '0.975rem', marginLeft: '170px',marginTop:'10px' }}>
                                        {regionError}
                                    </div>
                                )}
                                <div className="form-group">
                                    <Button type="submit" appearance="primary" style={{ marginTop: 20, marginLeft: 40 }}>
                                        Submit
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : !showResult ? (
                    <div className="container-fluid">
                        <div className="card-wrapper">
                            <form className="form" onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label htmlFor="code-authorize" className="form-label">Code to authorize*</label>
                                    <TextBox
                                        id="code-authorize"
                                        defaultValue=""
                                        placeholder="Enter Code to authorize"
                                        required
                                        separateLabel={false}
                                        className={`form-input ${codeAuthorizeError ? 'is-invalid' : ''}`}
                                        ref={codeAuthorizeRef}
                                    />
                                </div>
                                {codeAuthorizeError && (
                                    <div className="invalid-feedback" style={{ color: 'red', fontSize: '0.875rem', marginLeft: '170px' }}>
                                        {codeAuthorizeError}
                                    </div>
                                )}
                                <div className="form-group">
                                    <Button type="submit" appearance="primary" style={{ marginTop: 20, marginLeft: 40 }}>
                                        Submit
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : (
                    <ResultTable initialCaseId={caseId} initialCodeAuthorize={codeAuthorize} apiData={apiData} tokeninfo = {tokenInfo} />
                )}
            </div>
        </ToastProvider>
    );
};

export default Home;
